<script lang="ts" generics="TCommitReceipt = undefined">
  import type { CTAOptions } from "@kontextwerk/web-sdk/ui/actions/cta/types"
  import type { Snippet } from "svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import StepActionCTA from "../../../step-action-cta"
  import type {
    AppointmentPlanningExternalActionState,
    AppointmentPlanningOperations,
    AppointmentPlanningSubmissionPhase,
    AppointmentPlanningSubmissionResult,
    AppointmentPlanningSubmitIntent,
  } from "../../../../_logic/appointments-contract"
  import { projectAppointmentPlanningCommitOutcome } from "./project-submit-outcome"

  type Props = {
    children?: Snippet<[Snippet]>
    submitOptions?: CTAOptions
    externalActionState?: AppointmentPlanningExternalActionState
    intent: AppointmentPlanningSubmitIntent
    operations: AppointmentPlanningOperations<TCommitReceipt>
    onSubmissionResult: (
      result: AppointmentPlanningSubmissionResult<TCommitReceipt>,
    ) => void | Promise<void>
    onPhaseChange?: (phase: AppointmentPlanningSubmissionPhase) => void
  }

  const LONG_RUNNING_BATCH_NOTIFICATION_THRESHOLD = 50
  const performanceNow = () =>
    typeof globalThis.performance?.now === "function" ? globalThis.performance.now() : Date.now()
  const durationMs = (startedAt: number) => Math.round(performanceNow() - startedAt)
  const logTiming = (event: string, fields: Record<string, unknown>) => {
    globalThis.console?.info?.(event, fields)
  }

  let {
    children = null,
    intent,
    submitOptions = undefined,
    externalActionState = undefined,
    operations,
    onSubmissionResult,
    onPhaseChange = () => {},
  }: Props = $props()

  let running = $state(false)
  let phase = $state<AppointmentPlanningSubmissionPhase>("idle")

  const content = $derived(phase === "planning" ? "Plane Termine…" : intent.presentation.label)
  const disabled = $derived(
    running ||
      externalActionState?.blocksPlanning ||
      externalActionState?.pending ||
      intent.presentation.disabled,
  )
  const disabledReason = $derived(
    running
      ? "Termine werden gerade übernommen."
      : externalActionState?.blocksPlanning || externalActionState?.pending
        ? externalActionState.reason
        : intent.presentation.disabledReason,
  )

  const options = $derived(
    submitOptions
      ? {
          ...submitOptions,
          disabled: Boolean(running || externalActionState?.pending || submitOptions.disabled),
          disabledReason: running
            ? "Termine werden gerade übernommen."
            : externalActionState?.pending
              ? externalActionState.reason
              : submitOptions.disabledReason,
          items: submitOptions.items.map((item) => ({
            ...item,
            onClick: () => {
              // Also guard an already open menu and clicks in the same render turn.
              if (
                running ||
                externalActionState?.pending ||
                submitOptions?.disabled ||
                item.disabled
              )
                return

              return item.onClick?.()
            },
          })),
        }
      : undefined,
  )

  const changePhase = (next: AppointmentPlanningSubmissionPhase) => {
    phase = next
    onPhaseChange(next)
  }

  const notifyCommitResult = (
    result: Extract<AppointmentPlanningSubmissionResult<TCommitReceipt>, { kind: "committed" }>,
  ) => {
    const scheduledCount = result.response.scheduled_events?.length ?? 0

    if (scheduledCount) {
      newNotification({ class: "success", html: `Termine geplant: ${scheduledCount}.` })
    }

    if (result.status === "partial") {
      newNotification({
        class: "warning",
        html: result.response.scheduled_events.length
          ? "Einige Termine konnten nicht geplant werden. Die übrigen Zeilen können erneut bearbeitet werden."
          : "Termine konnten nicht geplant werden. Die Zeilen können erneut bearbeitet werden.",
      })
    }
  }

  const emitContinued = async (
    currentIntent: Extract<AppointmentPlanningSubmitIntent, { kind: "continue" }>,
  ) => {
    try {
      await onSubmissionResult({ kind: "continued", result: currentIntent.result })
    } catch (error) {
      console.error("appointment_planning_step_transition_failed", error)
      newNotification({ class: "error", html: "Schritt konnte nicht fortgesetzt werden." })
    }
  }

  const emitRemoved = async (
    currentIntent: Extract<AppointmentPlanningSubmitIntent, { kind: "remove_only" }>,
  ) => {
    try {
      await onSubmissionResult({
        kind: "removed",
        result: currentIntent.result,
        removedRowIds: [...currentIntent.removedRowIds],
      })
    } catch (error) {
      console.error("appointment_planning_row_removal_transition_failed", error)
      newNotification({
        class: "error",
        html: "Entfernte Zeilen konnten nicht in den Workflow übernommen werden.",
      })
    }
  }

  const run = async () => {
    if (running || externalActionState?.pending || externalActionState?.blocksPlanning) return

    if (intent.kind === "blocked") {
      if (intent.message) newNotification({ class: "error", html: intent.message })

      return
    }

    running = true

    if (intent.kind === "continue") {
      try {
        await emitContinued(intent)
      } finally {
        running = false
      }

      return
    }

    if (intent.kind === "remove_only") {
      try {
        await emitRemoved(intent)
      } finally {
        running = false
      }

      return
    }

    if (intent.rowCount > LONG_RUNNING_BATCH_NOTIFICATION_THRESHOLD) {
      newNotification({
        class: "info",
        html: "Es werden gerade mehr als 50 Aufträge geplant. Das kann einen Moment dauern.",
      })
    }

    changePhase("planning")
    const commitStartedAt = performanceNow()
    try {
      let operationResult: Awaited<ReturnType<typeof operations.commit>>
      try {
        operationResult = await operations.commit(intent.payload)
      } catch (error) {
        console.error("appointment_planning_commit_failed", error)
        newNotification({
          class: "error",
          html: "Termine konnten nicht geplant werden.",
        })

        return
      }

      const result = projectAppointmentPlanningCommitOutcome({
        intent,
        response: operationResult.response,
        receipt: operationResult.receipt,
      })
      logTiming("appointment_planning_surface_commit_timing", {
        row_count: intent.rowCount,
        scheduled_count: operationResult.response.scheduled_events?.length ?? 0,
        failed_count: operationResult.response.failed?.length ?? 0,
        duration_ms: durationMs(commitStartedAt),
      })
      notifyCommitResult(result)

      const transitionStartedAt = performanceNow()
      try {
        await onSubmissionResult(result)
        logTiming("appointment_planning_step_transition_timing", {
          row_count: intent.rowCount,
          duration_ms: durationMs(transitionStartedAt),
        })
      } catch (error) {
        console.error("appointment_planning_post_commit_transition_failed", error)
        newNotification({
          class: "error",
          html: "Änderung gespeichert, Oberfläche konnte nicht aktualisiert werden.",
        })
      }
    } finally {
      running = false
      changePhase("idle")
    }
  }
</script>

{#snippet primaryAction()}
  <StepActionCTA
    type="primaryButton"
    {content}
    onClick={run}
    {disabled}
    {disabledReason}
    {options}
  />
{/snippet}

{#if children}
  {@render children(primaryAction)}
{:else}
  {@render primaryAction()}
{/if}
